import { create } from 'zustand';
// sessionStorage persistence removed — canvas state persists to DB via project
import {
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  type Connection,
} from '@xyflow/react';

/* ── Handle IDs (must match the JSX in each node component) ── */
export const HANDLES = {
  imageOut: 'image-out',
  videoOut: 'video-out',
  // Video node inputs
  startFrame: 'start-frame',
  endFrame: 'end-frame',
  styleRef: 'style-ref',
  videoIn: 'video-in', // video-to-video extend
  // Edit/Text node input
  editSource: 'edit-source',
  textSource: 'text-source',
  // Blend node inputs
  blendIn: 'blend-in', // multi-connect
  // Upscale node input
  upscaleIn: 'upscale-in',
  // Generate image extract mode input
  extractVideoIn: 'extract-video-in',
  // Caption node input
  captionVideoIn: 'caption-video-in',
} as const;

/* ── Node data payloads ── */
export interface GenerationEntry {
  image?: string;       // base64 or URL
  video?: string;       // URL
  mimeType?: string;
  assetId?: string;
  gcsPath?: string;
  prompt?: string;      // prompt used for this generation
  timestamp: number;
}

export interface GenerateNodeData {
  label: string;
  mode?: 'generate' | 'extract'; // generate (AI) or extract (from video frame)
  prompt: string;
  aspectRatio: '1:1' | '3:4' | '4:3' | '9:16' | '16:9';
  style?: string;
  cameraAngle?: string;
  focalLength?: string;
  depthOfField?: string;
  extractTimestamp?: 'last' | 'first' | string; // for extract mode
  sourceVideoJobId?: string; // for extract mode — connected video's jobId
  resultImage?: string; // base64 or URL — derived from selected generation
  resultMimeType?: string;
  assetId?: string; // project asset id
  gcsPath?: string;
  isGenerating?: boolean;
  generations?: GenerationEntry[];
  selectedGeneration?: number; // index into generations[]
  [key: string]: unknown;
}

export interface EditNodeData {
  label: string;
  instruction: string;
  resultImage?: string;
  resultMimeType?: string;
  assetId?: string;
  isGenerating?: boolean;
  generations?: GenerationEntry[];
  selectedGeneration?: number;
  [key: string]: unknown;
}

export interface BlendNodeData {
  label: string;
  blendMode?: 'ai' | 'overlay';
  instruction: string;
  overlayScale?: number;
  overlayX?: string;
  overlayY?: string;
  overlayOpacity?: number;
  resultImage?: string;
  resultMimeType?: string;
  assetId?: string;
  gcsPath?: string;
  isGenerating?: boolean;
  generations?: GenerationEntry[];
  selectedGeneration?: number;
  [key: string]: unknown;
}

export interface SubtitleEntry {
  id: string;
  text: string;
  startMs: number;
  endMs: number;
  effect?: 'none' | 'fade';
  fadeMs?: number;
  x?: number;
  y?: number;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  backgroundColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
}

export interface CaptionNodeData {
  label: string;
  /** Transcribed subtitle entries (editable) */
  subtitles?: SubtitleEntry[];
  /** Whether transcription is in progress */
  isTranscribing?: boolean;
  /** Whether burn is in progress */
  isBurning?: boolean;
  /** Source video URL (from connected VideoNode) */
  sourceVideo?: string;
  /** Burned video URL (output) */
  resultVideo?: string;
  /** Status message for the user */
  statusMessage?: string;
  /** Whether audio was detected */
  hasAudio?: boolean;
  [key: string]: unknown;
}

export interface VideoNodeData {
  label: string;
  prompt: string;
  aspectRatio: '16:9' | '9:16';
  duration: '4s' | '6s' | '8s';
  enhancePrompt?: boolean;
  /** Which video AI to use. Default: 'veo' */
  provider?: 'veo' | 'sora';
  /** Veo quality tier. Default: 'fast' */
  quality?: 'lite' | 'fast' | 'standard';
  subtitles?: SubtitleEntry[];
  resultVideo?: string; // URL — derived from selected generation
  jobId?: string;
  /** The studio-store asset this node recorded, so a late server id can be linked to it. */
  storeAssetId?: string;
  operationId?: string;
  status?: 'idle' | 'generating' | 'processing' | 'completed' | 'failed';
  error?: string;
  generations?: GenerationEntry[];
  selectedGeneration?: number;
  [key: string]: unknown;
}

export interface TextOverlayNodeData {
  label: string;
  text: string;
  preset: 'headline' | 'cta' | 'caption' | 'priceTag' | 'watermark' | 'banner';
  x: number;
  y: number;
  fontSize: number;
  fontFamily?: string;
  color: string;
  backgroundColor: string;
  strokeColor: string;
  strokeWidth: number;
  shadow?: boolean;
  align?: 'left' | 'center' | 'right';
  resultImage?: string;
  resultMimeType?: string;
  assetId?: string;
  isGenerating?: boolean;
  generations?: GenerationEntry[];
  selectedGeneration?: number;
  [key: string]: unknown;
}

export interface UpscaleNodeData {
  label: string;
  scaleFactor: '2x' | '4x';
  resultImage?: string;
  resultMimeType?: string;
  assetId?: string;
  isGenerating?: boolean;
  [key: string]: unknown;
}

export interface UploadNodeData {
  label: string;
  resultImage?: string;
  resultMimeType?: string;
  gcsPath?: string;
  assetId?: string;
  isUploading?: boolean;
  originalName?: string;
  [key: string]: unknown;
}

export interface AssetNodeData {
  label: string;
  assetType: 'image' | 'video';
  dataUrl: string; // signed URL or base64
  mimeType: string;
  prompt?: string;
  serverId?: string; // project asset ID
  [key: string]: unknown;
}

export type StudioNodeData =
  | GenerateNodeData
  | EditNodeData
  | BlendNodeData
  | VideoNodeData
  | CaptionNodeData
  | TextOverlayNodeData
  | UpscaleNodeData
  | UploadNodeData
  | AssetNodeData
  | { label: string; stripAudio: boolean; [key: string]: unknown };

/* ── Validation: which handles can connect ── */
const VALID_CONNECTIONS: Record<string, string[]> = {
  [HANDLES.imageOut]: [
    HANDLES.startFrame,
    HANDLES.endFrame,
    HANDLES.styleRef,
    HANDLES.editSource,
    HANDLES.textSource,
    HANDLES.blendIn,
    HANDLES.upscaleIn,
  ],
  [HANDLES.videoOut]: [HANDLES.videoIn, HANDLES.captionVideoIn, HANDLES.extractVideoIn, 'concat-in'],
};

/* ── Store ── */
interface CanvasState {
  nodes: Node<StudioNodeData>[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  isValidConnection: (connection: Connection | Edge) => boolean;

  addNode: (node: Node<StudioNodeData>) => void;
  updateNodeData: (nodeId: string, data: Partial<StudioNodeData>) => void;
  removeNode: (nodeId: string) => void;
  getConnectedInputs: (nodeId: string) => { handle: string; sourceNode: Node<StudioNodeData> }[];
  reset: () => void;
}

let _nodeCounter = 0;
export const makeNodeId = (type: string) => `${type}-${Date.now()}-${++_nodeCounter}`;

export const useCanvasStore = create<CanvasState>()(
    (set, get) => ({
  nodes: [],
  edges: [],

  onNodesChange: (changes) =>
    set((s) => ({ nodes: applyNodeChanges(changes, s.nodes) as Node<StudioNodeData>[] })),

  onEdgesChange: (changes) =>
    set((s) => ({ edges: applyEdgeChanges(changes, s.edges) })),

  onConnect: (connection) =>
    set((s) => ({
      edges: addEdge(
        { ...connection, animated: true, style: { stroke: '#90caf9', strokeWidth: 2 } },
        s.edges
      ),
    })),

  isValidConnection: (connection) => {
    const sourceHandle = connection.sourceHandle || '';
    const targetHandle = connection.targetHandle || '';
    const allowed = VALID_CONNECTIONS[sourceHandle];
    if (!allowed) return false;
    return allowed.includes(targetHandle);
  },

  addNode: (node) => set((s) => ({ nodes: [...s.nodes, node] })),

  updateNodeData: (nodeId, data) =>
    set((s) => ({
      nodes: s.nodes.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n
      ) as Node<StudioNodeData>[],
    })),

  removeNode: (nodeId) =>
    set((s) => ({
      nodes: s.nodes.filter((n) => n.id !== nodeId),
      edges: s.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
    })),

  getConnectedInputs: (nodeId) => {
    const { nodes, edges } = get();
    return edges
      .filter((e) => e.target === nodeId)
      .map((e) => ({
        handle: e.targetHandle || '',
        sourceNode: nodes.find((n) => n.id === e.source)!,
      }))
      .filter((x) => x.sourceNode);
  },

  reset: () => set({ nodes: [], edges: [] }),
    })
);
